import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const MobilityAndUX = () => {
  const phrases = [
    "Native Edge Design",
    "Fluid Motion Systems",
    "Context-Aware Interfaces"
  ];

  const cardData = [
    { type: 'cyan-glass', content: '📱' },
    { type: 'fluid-wave', content: '✨' },
    { type: 'elastic-stack', content: '🎯' },
    { type: 'motion-blur', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "📱", title: "Cross-Platform Mobile Architecture", desc: "Build high-performance, single-codebase native experiences using Flutter and React Native that execute smoothly across both iOS and Android ecosystems." },
    { icon: "✨", title: "Micro-Interactions & Fluid Motion", desc: "Craft intuitive, spring-physics-based UI animations and tactile feedback rules that respond instantly to complex multi-finger gestures." },
    { icon: "🌐", title: "Responsive & Adaptive Layout Systems", desc: "Engineer design token matrices that scale layout components naturally across dynamic mobile screens, tablets, foldables, and wear platforms." },
    { icon: "⚙️", title: "Offline-First Synchronization Nodes", desc: "Deploy resilient client-side storage layers and background workers that process telemetry locally and sync smoothly during poor network coverage." },
    { icon: "♿", title: "Inclusive WCAG & Mobile Accessibility", desc: "Enforce deep semantic structures, optimal touch target hitboxes, high-contrast modes, and screen-reader optimizations from day one." },
    { icon: "⚡", title: "Core Edge Performance Optimization", desc: "Minimize asset bundles, optimize image compression engines, and structure memory caches to guarantee consistent 60FPS scrolling." },
    { icon: "🎨", title: "Design System & Asset Federation", desc: "Establish modular Figma-to-code components to bridge design intent with product delivery, keeping UI themes uniform across internal systems." }
  ];

  const advancedServices = [
    { icon: "🧭", title: "Location-Based & Geofencing Contexts", desc: "Integrate background hardware positioning loops to calculate context-aware proximity features without draining system battery life." },
    { icon: "🔒", title: "Biometric Hardware Authentication", desc: "Deploy native FaceID and fingerprint biometric authenticators inside transaction screens, maximizing mobile security pipelines." },
    { icon: "📈", title: "Granular Product Interaction Telemetry", desc: "Embed programmatic heatmap captures and touch tracking utilities to identify drop-off barriers without recording private user credentials." },
    { icon: "🎙️", title: "Conversational & Voice UI Modules", desc: "Incorporate voice-driven actions and speech-to-text engines to facilitate hands-free application navigation for field workers." },
    { icon: "🏎️", title: "Instant Native Device Bridge Access", desc: "Develop low-level C++ or Kotlin/Swift bridge layers to interface directly with custom Bluetooth LE modules and local peripherals." },
    { icon: "🔔", title: "Intelligent Rich Push Notification Trees", desc: "Design segmented background payload routing systems that deliver rich media alerts directly to lockscreens based on regional user activity." },
    { icon: "🔄", title: "Automated Device Matrix Testing", desc: "Run continuous UI automation scripts across physical cloud device arrays to catch visual rendering breaking changes before release tags." },
    { icon: "👓", title: "Spatial UX & Augmented Reality Layouts", desc: "Draft localized camera viewport templates to project complex spatial contextual interfaces directly onto real-world assets." },
    { icon: "🧪", title: "A/B Layout Split Verification Systems", desc: "Deploy dynamic component replacement layers to test distinct design approaches against primary user conversion parameters simultaneously." }
  ];

  const technologies = {
    "Development Frameworks": ["React Native / Expo", "Flutter / Dart SDK", "Native iOS (Swift / SwiftUI)", "Native Android (Kotlin / Jetpack)"],
    "Design & Prototyping": ["Figma Advanced Tokenization", "Adobe Creative Suite", "ProtoPie Interactive Engine", "Principle / Framer Motion"],
    "State & Storage Kernels": ["Redux Toolkit / Zustand", "SQLite / Room DB Engine", "MMKV High-Speed Storage", "WatermelonDB Local Sync"],
    "Architecture Tools": ["GraphQL / Apollo Client", "Firebase Suite / Cloud Functions", "Sentry Performance Logs", "Codemagic / Bitrise CI Matrix"],
    "Testing & Quality Assurance": ["Appium Automation Suite", "Detox E2E Test Framework", "Maestro UI Verification", "Lighthouse Mobile Auditing"]
  };

  const processSteps = [
    { num: "01", title: "User Research & Auditing", desc: "Analyzing user environments, discovering localized cellular limitations, and detailing interactive operational goals." },
    { num: "02", title: "Wireframing & Touch Architecture", desc: "Structuring thumb-zone reach matrices, mapping view stacks, and configuring layout patterns." },
    { num: "03", title: "Interactive Design Systems", desc: "Building consistent dynamic typography sheets, icon fonts, and defining strict functional spacing guides." },
    { num: "04", title: "Frontend Edge Engineering", desc: "Writing highly responsive component layouts, setting up animations, and connecting local hardware APIs." },
    { num: "05", title: "Offline Storage Hardening", desc: "Configuring local databases, caching behaviors, and creating automated transaction synchronization routines." },
    { num: "06", title: "Automated Matrix Verification", desc: "Testing target application flows across various system releases and display resolutions to guarantee rendering accuracy." },
    { num: "07", title: "Performance Analysis & Tuning", desc: "Tracking application initialization speeds, dropping render lag, and scaling interactions based on live analytics." }
  ];

  const industries = [
    "Logistics Tracking & Final-Mile Delivery", "On-the-Go Micro-Fintech Platforms", "Patient-Facing Healthcare Telemedicine",
    "Connected IoT Consumer Devices", "Field Service Diagnostics & Operations", "High-Volume Retail & E-Commerce Mobile",
    "Digital Media Streaming Applications", "Industrial Enterprise Inventory Platforms", "Smart Travel & Transit Networks",
    "Dynamic Asset Tracking Systems", "On-Demand Resource Sharing Networks", "Enterprise Workforce Management Solutions"
  ];

  const designBenefits = [
    "Sub-100ms UI Transaction Renderings", "Drastic Reduction in User Interaction Friction", "Zero-Loss Complete Offline Functionality",
    "Flawless Cross-Platform UI Consistency", "Airtight Native Device Hardware Security", "Increased Play Store & App Store Ratings",
    "Accelerated Time-to-Market Release Speeds", "Fully Accessible WCAG Standard Layouts"
  ];

  const whyChooseUs = [
    "Expert Native Mobile Engineers", "Validated Component Asset Collections", "Human-Centered UX Design Methodology",
    "Deep Edge-to-Cloud API Integration", "Rigorous Performance Architecture Rules", "Proactive Interaction Pattern Verification",
    "Continuous App Store Deployment Services", "Transparent Component Maintenance Lifecycles", "Dedicated Accessibility Quality Assurance",
    "Experienced Graphic & Interface Illustrators", "Iterative Sprint-Based Delivery Models", "100% Native Edge Optimization Focus"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Mobility & UX Services | Mobile UX/UI Design & Digital Experience | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Mobility & UX Services | Mobile UX/UI Design & Digital Experience | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers Mobility and UX services, including mobile app UX/UI design, user experience consulting, user interface design, responsive design, digital experience solutions, usability testing, wireframing, prototyping, and cross-platform mobility solutions."
        />

        <meta
          name="keywords"
          content="Mobility and UX, Mobility Services, UX Design, UI Design, UX UI Design, Mobile UX Design, Mobile UI Design, User Experience Design, User Interface Design, Responsive Design, Digital Experience, Mobile App Design, Wireframing, Prototyping, Usability Testing, Cross Platform Mobility, UX Consulting, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/mobility-and-ux"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Mobility & UX Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Create exceptional digital experiences with Mobility and UX services from Affsoft IT Solutions Pvt. Ltd. We specialize in UX/UI design, mobile app design, usability testing, wireframing, prototyping, and responsive user experiences."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/mobility-and-ux"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Mobility & UX Services | Mobile UX/UI Design | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Deliver seamless mobile and digital experiences with Mobility and UX services from Affsoft IT Solutions Pvt. Ltd. We provide UX/UI design, responsive interfaces, mobile app design, and user-centered digital solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="mobility-ux-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f172a' // Crisp slate-blue context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Mobility &nbsp; and &nbsp;</span>
              <span>User Experience Engineering </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-cyan fs-3" style={{ color: '#06b6d4' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Mobility & UX</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we engineer mobile applications that are fast, intuitive, and reliable. Our design methodology pairs responsive structural design patterns with optimized native development matrices to transform mobile interfaces into smooth, gesture-driven digital products built to perform anywhere.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Audit Your App Interface</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#06b6d4' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#06b6d4' }}>Interface Engineering</span>
              <h2 className="fw-bold text-dark display-6">Create Tactile, High-Fidelity Mobile Experiences</h2>
              <p className="text-muted mt-3">
                Sluggish view rendering, clumsy navigation setups, and lack of offline accessibility frustrate users and drive up platform churn. At Affsoft IT Solutions Pvt. Ltd., we focus on building performant mobile systems designed to simplify user paths.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#06b6d4' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Structural Advantages of Optimized Mobility Development</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#06b6d4' }}>Hardware & Interaction Systems</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Device Capabilities Crafted for Peak Retention</h2>
              <p className="text-muted mt-3">
                We extend standard front-end frameworks into context-aware systems. From utilizing low-level biometric sensors to managing custom device integrations, our templates ensure total runtime control.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#06b6d4' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#22d3ee' }}>Integration Architecture Matrix</span>
              <h2 className="fw-bold display-6">Ecosystem Design & Development Toolsets We Leverage</h2>
              <p className="text-light-50">We structure, package, and launch production mobility systems using industry-standard environments.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#22d3ee' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#22d3ee' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Design Methodology</span>
              <h2 className="fw-bold text-dark display-6">Our Mobility Implementation Blueprint</h2>
              <p className="text-muted">A clear, disciplined pipeline framework designed to move your interface smoothly from concept verification onto public production updates.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#06b6d4' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#06b6d4' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine human-centered interactive principles with optimized native performance architectures to safeguard your product delivery.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#06b6d4' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default MobilityAndUX;